import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { format, parseISO, subDays } from 'date-fns';
import {
    AreaChart,
    Area,
    LineChart as RechartsLineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    ReferenceLine,
    ReferenceArea,
    Legend,
    ComposedChart
} from 'recharts';
import { 
    Activity, 
    BedDouble, 
    Utensils, 
    FlameKindling, 
    TestTube2, 
    CalendarClock,
    TrendingUp,
    TrendingDown,
    Filter,
    Clock,
    AlertTriangle,
    Sparkles,
    ChevronDown,
    ChevronUp,
    Zap,
    Moon,
    Pill,
    Search,
    Info,
    Flame,
    CheckCircle2,
    XCircle,
    Heart,
    Syringe,
    LineChart,
    Plus,
    X,
    BarChart3,
    Paperclip
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_CONFIG = {
    symptom: { 
        icon: Activity, 
        color: 'text-rose-500', 
        bg: 'bg-rose-50 dark:bg-rose-950/30',
        border: 'border-rose-200 dark:border-rose-800',
        label: 'Symptoms'
    },
    sleep: { 
        icon: BedDouble, 
        color: 'text-indigo-500', 
        bg: 'bg-indigo-50 dark:bg-indigo-950/30',
        border: 'border-indigo-200 dark:border-indigo-800',
        label: 'Sleep'
    },
    food: { 
        icon: Utensils, 
        color: 'text-emerald-500', 
        bg: 'bg-emerald-50 dark:bg-emerald-950/30',
        border: 'border-emerald-200 dark:border-emerald-800',
        label: 'Food'
    },
    flare: { 
        icon: FlameKindling, 
        color: 'text-orange-500', 
        bg: 'bg-orange-50 dark:bg-orange-950/30',
        border: 'border-orange-200 dark:border-orange-800',
        label: 'Flare'
    },
    lab: { 
        icon: TestTube2, 
        color: 'text-purple-500', 
        bg: 'bg-purple-50 dark:bg-purple-950/30',
        border: 'border-purple-200 dark:border-purple-800',
        label: 'Lab Results'
    },
    appointment: { 
        icon: CalendarClock, 
        color: 'text-blue-500', 
        bg: 'bg-blue-50 dark:bg-blue-950/30',
        border: 'border-blue-200 dark:border-blue-800',
        label: 'Appointment'
    },
    cycle: { 
        icon: Heart, 
        color: 'text-pink-500', 
        bg: 'bg-pink-50 dark:bg-pink-950/30',
        border: 'border-pink-200 dark:border-pink-800',
        label: 'Cycle'
    },
    vaccine: { 
        icon: Syringe, 
        color: 'text-teal-500', 
        bg: 'bg-teal-50 dark:bg-teal-950/30',
        border: 'border-teal-800',
        label: 'Vaccine'
    }
};

// Event type colors for the graph
const EVENT_COLORS = {
    supplement: '#22c55e', // green
    medication: '#a855f7', // purple
    flare: '#ef4444',      // red
    vaccine: '#14b8a6',    // teal
    lab: '#3b82f6',        // blue
    appointment: '#6366f1' // indigo
};

// Event category configuration for the treatment list
const EVENT_CATEGORIES = {
    supplement: { label: 'Supplements', color: 'text-green-500', bg: 'bg-green-500', icon: '▶' },
    medication: { label: 'Medications', color: 'text-purple-500', bg: 'bg-purple-500', icon: '▶' },
    flare: { label: 'Flares', color: 'text-red-500', bg: 'bg-red-500', icon: '●' },
    vaccine: { label: 'Vaccines', color: 'text-teal-500', bg: 'bg-teal-500', icon: '▶' },
    lab: { label: 'Lab Results', color: 'text-blue-500', bg: 'bg-blue-500', icon: '◆' },
    appointment: { label: 'Appointments', color: 'text-indigo-500', bg: 'bg-indigo-500', icon: '■' }
};

// Quick Add Modal Component
const QuickAddModal = ({ isOpen, onClose, type, token, onSuccess }) => {
    const [formData, setFormData] = useState({
        name: '',
        dosage: '',
        frequency: 'daily',
        date: format(new Date(), 'yyyy-MM-dd'),
        notes: '',
        item_type: 'supplement',
        vaccine_name: '',
        manufacturer: '',
        title: '',
        doctor: '',
        location: '',
        time: '09:00'
    });
    const [saving, setSaving] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);

        try {
            let endpoint = '';
            let body = {};

            if (type === 'supplement' || type === 'medication') {
                endpoint = `${API_URL}/api/supplements`;
                body = {
                    name: formData.name,
                    dosage: formData.dosage,
                    frequency: formData.frequency,
                    item_type: type,
                    active: true,
                    notes: formData.notes
                };
            } else if (type === 'vaccine') {
                endpoint = `${API_URL}/api/vaccines`;
                body = {
                    vaccine_name: formData.vaccine_name || formData.name,
                    date_administered: formData.date,
                    manufacturer: formData.manufacturer,
                    notes: formData.notes
                };
            } else if (type === 'appointment') {
                endpoint = `${API_URL}/api/appointments`;
                body = {
                    title: formData.title || formData.name,
                    date: formData.date,
                    time: formData.time,
                    doctor: formData.doctor,
                    location: formData.location,
                    notes: formData.notes
                };
            }

            const res = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(body)
            });

            if (res.ok) {
                toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} added successfully!`);
                onSuccess();
                onClose();
                setFormData({
                    name: '',
                    dosage: '',
                    frequency: 'daily',
                    date: format(new Date(), 'yyyy-MM-dd'),
                    notes: '',
                    item_type: 'supplement',
                    vaccine_name: '',
                    manufacturer: '',
                    title: '',
                    doctor: '',
                    location: '',
                    time: '09:00'
                });
            } else {
                const error = await res.json();
                toast.error(error.detail || 'Failed to add entry');
            }
        } catch (error) {
            console.error('Error adding entry:', error);
            toast.error('Failed to add entry');
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) return null;

    const getTitle = () => {
        switch (type) {
            case 'supplement': return 'Add Supplement';
            case 'medication': return 'Add Medication';
            case 'vaccine': return 'Add Vaccine';
            case 'appointment': return 'Add Appointment';
            default: return 'Add Entry';
        }
    };

    const getIcon = () => {
        switch (type) {
            case 'supplement':
            case 'medication':
                return <Pill className="w-5 h-5" />;
            case 'vaccine':
                return <Syringe className="w-5 h-5" />;
            case 'appointment':
                return <CalendarClock className="w-5 h-5" />;
            default:
                return <Plus className="w-5 h-5" />;
        }
    };

    const getColor = () => {
        switch (type) {
            case 'supplement': return 'text-green-500';
            case 'medication': return 'text-purple-500';
            case 'vaccine': return 'text-teal-500';
            case 'appointment': return 'text-indigo-500';
            default: return 'text-slate-500';
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-900 border border-slate-700 rounded-xl max-w-md w-full shadow-2xl">
                <div className="flex items-center justify-between p-4 border-b border-slate-700">
                    <div className="flex items-center gap-2">
                        <span className={getColor()}>{getIcon()}</span>
                        <h3 className="text-lg font-semibold text-white">{getTitle()}</h3>
                    </div>
                    <Button variant="ghost" size="sm" onClick={onClose} className="text-slate-400 hover:text-white">
                        <X className="w-5 h-5" />
                    </Button>
                </div>
                <form onSubmit={handleSubmit} className="p-4 space-y-4">
                    {(type === 'supplement' || type === 'medication') && (
                        <>
                            <div>
                                <Label className="text-slate-300">Name *</Label>
                                <Input
                                    required
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    placeholder={type === 'supplement' ? 'e.g., Vitamin D3' : 'e.g., Prednisone'}
                                    className="bg-slate-800 border-slate-600 text-white"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="text-slate-300">Dosage</Label>
                                    <Input
                                        value={formData.dosage}
                                        onChange={(e) => setFormData({ ...formData, dosage: e.target.value })}
                                        placeholder="e.g., 5000 IU"
                                        className="bg-slate-800 border-slate-600 text-white"
                                    />
                                </div>
                                <div>
                                    <Label className="text-slate-300">Frequency</Label>
                                    <Select value={formData.frequency} onValueChange={(v) => setFormData({ ...formData, frequency: v })}>
                                        <SelectTrigger className="bg-slate-800 border-slate-600 text-white">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="daily">Daily</SelectItem>
                                            <SelectItem value="twice_daily">Twice Daily</SelectItem>
                                            <SelectItem value="weekly">Weekly</SelectItem>
                                            <SelectItem value="as_needed">As Needed</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </>
                    )}

                    {type === 'vaccine' && (
                        <>
                            <div>
                                <Label className="text-slate-300">Vaccine Name *</Label>
                                <Input
                                    required
                                    value={formData.vaccine_name}
                                    onChange={(e) => setFormData({ ...formData, vaccine_name: e.target.value })}
                                    placeholder="e.g., Flu Shot, COVID-19"
                                    className="bg-slate-800 border-slate-600 text-white"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="text-slate-300">Date Administered *</Label>
                                    <Input
                                        type="date"
                                        required
                                        value={formData.date}
                                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                                        className="bg-slate-800 border-slate-600 text-white"
                                    />
                                </div>
                                <div>
                                    <Label className="text-slate-300">Manufacturer</Label>
                                    <Input
                                        value={formData.manufacturer}
                                        onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })}
                                        placeholder="e.g., Pfizer"
                                        className="bg-slate-800 border-slate-600 text-white"
                                    />
                                </div>
                            </div>
                        </>
                    )}

                    {type === 'appointment' && (
                        <>
                            <div>
                                <Label className="text-slate-300">Appointment Title *</Label>
                                <Input
                                    required
                                    value={formData.title}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                    placeholder="e.g., Rheumatologist Follow-up"
                                    className="bg-slate-800 border-slate-600 text-white"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="text-slate-300">Date *</Label>
                                    <Input
                                        type="date"
                                        required
                                        value={formData.date}
                                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                                        className="bg-slate-800 border-slate-600 text-white"
                                    />
                                </div>
                                <div>
                                    <Label className="text-slate-300">Time</Label>
                                    <Input
                                        type="time"
                                        value={formData.time}
                                        onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                                        className="bg-slate-800 border-slate-600 text-white"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="text-slate-300">Doctor</Label>
                                    <Input
                                        value={formData.doctor}
                                        onChange={(e) => setFormData({ ...formData, doctor: e.target.value })}
                                        placeholder="Dr. Smith"
                                        className="bg-slate-800 border-slate-600 text-white"
                                    />
                                </div>
                                <div>
                                    <Label className="text-slate-300">Location</Label>
                                    <Input
                                        value={formData.location}
                                        onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                                        placeholder="City Medical Center"
                                        className="bg-slate-800 border-slate-600 text-white"
                                    />
                                </div>
                            </div>
                        </>
                    )}

                    <div>
                        <Label className="text-slate-300">Notes</Label>
                        <Textarea
                            value={formData.notes}
                            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                            placeholder="Any additional notes..."
                            className="bg-slate-800 border-slate-600 text-white"
                            rows={2}
                        />
                    </div>

                    <div className="flex gap-3 pt-2">
                        <Button type="button" variant="outline" onClick={onClose} className="flex-1 border-slate-600 text-slate-300">
                            Cancel
                        </Button>
                        <Button type="submit" disabled={saving} className="flex-1 bg-indigo-600 hover:bg-indigo-700">
                            {saving ? 'Saving...' : 'Save'}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// Custom tooltip for the graph
const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        const data = payload[0].payload;
        return (
            <div className="bg-slate-900 border border-slate-700 rounded-lg p-3 shadow-xl">
                <p className="text-white font-medium mb-1">{label}</p>
                <p className="text-orange-400">Severity: {data.severity || 'N/A'}</p>
                {data.symptoms && data.symptoms.length > 0 && (
                    <p className="text-slate-400 text-sm mt-1">
                        Symptoms: {data.symptoms.slice(0, 3).join(', ')}
                        {data.symptoms.length > 3 && '...'}
                    </p>
                )}
                {data.events && data.events.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-slate-700">
                        <p className="text-slate-300 text-xs">Events:</p>
                        {data.events.map((event, i) => (
                            <p key={i} className="text-xs" style={{ color: EVENT_COLORS[event.type] || '#fff' }}>
                                • {event.name}
                            </p>
                        ))}
                    </div>
                )}
            </div>
        );
    }
    return null;
};

// Graph View Component
const GraphView = ({ timeline, supplements, flares, loading, dateRange, token, onRefresh }) => {
    const [visibleEvents, setVisibleEvents] = useState({
        supplements: true,
        medications: true,
        flares: true,
        vaccines: true,
        labs: true,
        appointments: true
    });
    const [quickAddType, setQuickAddType] = useState(null);
    const [attachmentCounts, setAttachmentCounts] = useState({});

    // Process timeline data for the chart and build event list
    const { chartData, allEvents } = React.useMemo(() => {
        const dataByDate = {};
        const eventsList = [];
        
        // Process all timeline entries
        timeline.forEach(entry => {
            const date = entry.date;
            if (!dataByDate[date]) {
                dataByDate[date] = {
                    date,
                    dateFormatted: format(parseISO(date), 'MMM d'),
                    severity: null,
                    symptoms: [],
                    events: []
                };
            }
            
            if (entry.type === 'symptom') {
                dataByDate[date].severity = entry.severity || entry.overall_severity;
                dataByDate[date].symptoms = entry.symptoms || [];
            }
            
            if (entry.type === 'flare') {
                const eventName = `Flare (Severity: ${entry.severity})`;
                dataByDate[date].events.push({ type: 'flare', name: eventName });
                eventsList.push({ 
                    date, 
                    type: 'flare', 
                    name: 'Flare', 
                    displayName: eventName,
                    color: EVENT_COLORS.flare,
                    sortOrder: 3,
                    parentType: 'flare_journal',
                    parentId: entry.data?.id
                });
            }
            
            if (entry.type === 'vaccine') {
                const vaccineName = entry.vaccine_name || 'Vaccine';
                dataByDate[date].events.push({ type: 'vaccine', name: vaccineName });
                eventsList.push({ 
                    date, 
                    type: 'vaccine', 
                    name: vaccineName, 
                    displayName: vaccineName,
                    color: EVENT_COLORS.vaccine,
                    sortOrder: 4,
                    parentType: 'vaccine',
                    parentId: entry.data?.id
                });
            }
            
            if (entry.type === 'lab') {
                const labName = entry.name || 'Lab Result';
                dataByDate[date].events.push({ type: 'lab', name: `${labName}: ${entry.value}` });
                eventsList.push({ 
                    date, 
                    type: 'lab', 
                    name: labName, 
                    displayName: labName,
                    color: EVENT_COLORS.lab,
                    sortOrder: 5,
                    parentType: 'lab_result',
                    parentId: entry.data?.id
                });
            }
            
            if (entry.type === 'appointment') {
                const apptName = entry.title || 'Appointment';
                dataByDate[date].events.push({ type: 'appointment', name: apptName });
                eventsList.push({ 
                    date, 
                    type: 'appointment', 
                    name: apptName, 
                    displayName: apptName,
                    color: EVENT_COLORS.appointment,
                    sortOrder: 6,
                    parentType: 'appointment',
                    parentId: entry.data?.id
                });
            }
        });

        // Also process supplements data if available
        if (supplements && supplements.length > 0) {
            supplements.forEach(supp => {
                // Add supplement events from correlation data
                if (supp.name) {
                    eventsList.push({
                        date: null, // We don't have specific dates from correlation data
                        type: supp.is_medication ? 'medication' : 'supplement',
                        name: supp.name,
                        displayName: supp.name,
                        color: supp.is_medication ? EVENT_COLORS.medication : EVENT_COLORS.supplement,
                        sortOrder: supp.is_medication ? 2 : 1,
                        fromCorrelation: true
                    });
                }
            });
        }
        
        // Convert to array and sort by date
        const sortedData = Object.values(dataByDate)
            .sort((a, b) => new Date(a.date) - new Date(b.date));
        
        // Sort events: by date first, then by type
        const sortedEvents = eventsList
            .filter(e => e.date) // Only include events with dates for the list
            .sort((a, b) => {
                const dateCompare = new Date(a.date) - new Date(b.date);
                if (dateCompare !== 0) return dateCompare;
                return a.sortOrder - b.sortOrder;
            });
        
        return { chartData: sortedData, allEvents: sortedEvents };
    }, [timeline, supplements]);

    // Get reference lines for events on chart
    const referenceLines = React.useMemo(() => {
        const lines = [];
        
        allEvents.forEach((event, index) => {
            const isVisible = 
                (event.type === 'flare' && visibleEvents.flares) ||
                (event.type === 'vaccine' && visibleEvents.vaccines) ||
                (event.type === 'lab' && visibleEvents.labs) ||
                (event.type === 'appointment' && visibleEvents.appointments) ||
                (event.type === 'supplement' && visibleEvents.supplements) ||
                (event.type === 'medication' && visibleEvents.medications);
            
            if (isVisible && event.date) {
                lines.push({
                    x: format(parseISO(event.date), 'MMM d'),
                    color: event.color,
                    label: event.name,
                    type: event.type
                });
            }
        });
        
        return lines;
    }, [allEvents, visibleEvents]);

    // Group events by category for the treatment list display
    const groupedEvents = React.useMemo(() => {
        const groups = {
            supplement: [],
            medication: [],
            flare: [],
            vaccine: [],
            lab: [],
            appointment: []
        };
        
        allEvents.forEach(event => {
            if (groups[event.type]) {
                // Check visibility
                const typeKey = event.type === 'supplement' ? 'supplements' : 
                               event.type === 'medication' ? 'medications' :
                               event.type === 'flare' ? 'flares' :
                               event.type === 'vaccine' ? 'vaccines' :
                               event.type === 'lab' ? 'labs' : 'appointments';
                
                if (visibleEvents[typeKey]) {
                    groups[event.type].push(event);
                }
            }
        });
        
        return groups;
    }, [allEvents, visibleEvents]);

    const toggleEventType = (type) => {
        setVisibleEvents(prev => ({ ...prev, [type]: !prev[type] }));
    };

    // Bulk-fetch attachment counts for all events that map to a parent record
    useEffect(() => {
        const items = allEvents
            .filter(e => e.parentType && e.parentId)
            .map(e => ({ parent_type: e.parentType, parent_id: e.parentId }));
        if (items.length === 0) {
            setAttachmentCounts({});
            return;
        }
        const seen = new Set();
        const deduped = items.filter(it => {
            const k = `${it.parent_type}:${it.parent_id}`;
            if (seen.has(k)) return false;
            seen.add(k);
            return true;
        });
        let cancelled = false;
        (async () => {
            try {
                const { data } = await axios.post(`${API_URL}/api/attachments/bulk-counts`, { items: deduped });
                if (!cancelled) setAttachmentCounts(data.counts || {});
            } catch (e) {
                console.warn('Failed to fetch timeline attachment counts', e);
            }
        })();
        return () => { cancelled = true; };
    }, [allEvents]);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
        );
    }

    if (chartData.length === 0) {
        return (
            <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                    <LineChart className="w-12 h-12 text-muted-foreground/50 mb-4" />
                    <h3 className="font-medium text-lg mb-2">No data for graph</h3>
                    <p className="text-muted-foreground text-center max-w-md">
                        Start logging symptoms and other health data to see your trends visualized here.
                    </p>
                </CardContent>
            </Card>
        );
    }

    // Render a single event item in the list
    const renderEventItem = (event, index) => {
        const category = EVENT_CATEGORIES[event.type];
        const formattedDate = event.date ? format(parseISO(event.date), 'MMM d') : '';
        const attachCount = (event.parentType && event.parentId)
            ? (attachmentCounts[`${event.parentType}:${event.parentId}`] || 0)
            : 0;

        return (
            <div 
                key={`${event.type}-${event.name}-${event.date}-${index}`}
                className="flex items-center gap-2 text-sm"
                data-testid={`event-item-${event.type}-${index}`}
            >
                <span className={category.color}>{category.icon}</span>
                <span className="text-slate-200">{event.displayName || event.name}</span>
                {formattedDate && (
                    <span className="text-slate-500">({formattedDate})</span>
                )}
                {attachCount > 0 && (
                    <span
                        className="ml-auto inline-flex items-center gap-0.5 text-indigo-400 text-xs"
                        data-testid={`event-paperclip-${event.parentType}-${event.parentId}`}
                        title={`${attachCount} photo${attachCount === 1 ? '' : 's'} attached`}
                    >
                        <Paperclip className="w-3 h-3" />
                        {attachCount}
                    </span>
                )}
            </div>
        );
    };

    return (
        <div className="space-y-6">
            {/* Quick Add Modal */}
            <QuickAddModal
                isOpen={quickAddType !== null}
                onClose={() => setQuickAddType(null)}
                type={quickAddType}
                token={token}
                onSuccess={onRefresh}
            />

            {/* Quick Add Buttons */}
            <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-4">
                    <div className="flex flex-wrap items-center gap-3">
                        <span className="text-slate-400 text-sm font-medium">Quick Add:</span>
                        <Button
                            variant="outline"
                            size="sm"
                            className="border-green-600 text-green-500 hover:bg-green-600/20"
                            onClick={() => setQuickAddType('supplement')}
                            data-testid="quick-add-supplement"
                        >
                            <Pill className="w-4 h-4 mr-1" />
                            Supplement
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            className="border-purple-600 text-purple-500 hover:bg-purple-600/20"
                            onClick={() => setQuickAddType('medication')}
                            data-testid="quick-add-medication"
                        >
                            <Pill className="w-4 h-4 mr-1" />
                            Medication
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            className="border-teal-600 text-teal-500 hover:bg-teal-600/20"
                            onClick={() => setQuickAddType('vaccine')}
                            data-testid="quick-add-vaccine"
                        >
                            <Syringe className="w-4 h-4 mr-1" />
                            Vaccine
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            className="border-indigo-600 text-indigo-500 hover:bg-indigo-600/20"
                            onClick={() => setQuickAddType('appointment')}
                            data-testid="quick-add-appointment"
                        >
                            <CalendarClock className="w-4 h-4 mr-1" />
                            Appointment
                        </Button>
                    </div>
                </CardContent>
            </Card>

            <Card className="bg-slate-900 border-slate-700">
                <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                        <Activity className="w-5 h-5 text-orange-500" />
                        Treatment & Symptom Correlation
                    </CardTitle>
                    <CardDescription className="text-slate-400">
                        Track how treatments correlate with your symptom severity over time
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="h-[400px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart
                                data={chartData}
                                margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
                            >
                                <defs>
                                    <linearGradient id="severityGradient" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#f97316" stopOpacity={0.8}/>
                                        <stop offset="95%" stopColor="#f97316" stopOpacity={0.1}/>
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                                <XAxis 
                                    dataKey="dateFormatted" 
                                    stroke="#9ca3af"
                                    tick={{ fill: '#9ca3af', fontSize: 12 }}
                                />
                                <YAxis 
                                    domain={[0, 10]} 
                                    stroke="#9ca3af"
                                    tick={{ fill: '#9ca3af', fontSize: 12 }}
                                    label={{ value: 'Severity', angle: -90, position: 'insideLeft', fill: '#9ca3af' }}
                                />
                                <Tooltip content={<CustomTooltip />} />
                                
                                {/* Reference lines for events */}
                                {referenceLines.map((line, index) => (
                                    <ReferenceLine
                                        key={`${line.x}-${index}`}
                                        x={line.x}
                                        stroke={line.color}
                                        strokeWidth={2}
                                        strokeDasharray={line.type === 'lab' ? '5 5' : '0'}
                                    />
                                ))}
                                
                                <Area
                                    type="monotone"
                                    dataKey="severity"
                                    stroke="#f97316"
                                    strokeWidth={3}
                                    fill="url(#severityGradient)"
                                    connectNulls
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                    
                    {/* Event Type Filters */}
                    <div className="mt-6 pt-4 border-t border-slate-700">
                        <p className="text-slate-400 text-sm mb-3">Filter Event Types</p>
                        <div className="flex flex-wrap gap-2">
                            <Badge 
                                variant={visibleEvents.supplements ? "default" : "outline"}
                                className={`cursor-pointer ${visibleEvents.supplements ? 'bg-green-600 hover:bg-green-700' : 'border-green-600 text-green-500'}`}
                                onClick={() => toggleEventType('supplements')}
                                data-testid="filter-supplements"
                            >
                                <Pill className="w-3 h-3 mr-1" />
                                Supplements
                            </Badge>
                            <Badge 
                                variant={visibleEvents.medications ? "default" : "outline"}
                                className={`cursor-pointer ${visibleEvents.medications ? 'bg-purple-600 hover:bg-purple-700' : 'border-purple-600 text-purple-500'}`}
                                onClick={() => toggleEventType('medications')}
                                data-testid="filter-medications"
                            >
                                <Pill className="w-3 h-3 mr-1" />
                                Medications
                            </Badge>
                            <Badge 
                                variant={visibleEvents.flares ? "default" : "outline"}
                                className={`cursor-pointer ${visibleEvents.flares ? 'bg-red-600 hover:bg-red-700' : 'border-red-600 text-red-500'}`}
                                onClick={() => toggleEventType('flares')}
                                data-testid="filter-flares"
                            >
                                <FlameKindling className="w-3 h-3 mr-1" />
                                Flares
                            </Badge>
                            <Badge 
                                variant={visibleEvents.vaccines ? "default" : "outline"}
                                className={`cursor-pointer ${visibleEvents.vaccines ? 'bg-teal-600 hover:bg-teal-700' : 'border-teal-600 text-teal-500'}`}
                                onClick={() => toggleEventType('vaccines')}
                                data-testid="filter-vaccines"
                            >
                                <Syringe className="w-3 h-3 mr-1" />
                                Vaccines
                            </Badge>
                            <Badge 
                                variant={visibleEvents.labs ? "default" : "outline"}
                                className={`cursor-pointer ${visibleEvents.labs ? 'bg-blue-600 hover:bg-blue-700' : 'border-blue-600 text-blue-500'}`}
                                onClick={() => toggleEventType('labs')}
                                data-testid="filter-labs"
                            >
                                <TestTube2 className="w-3 h-3 mr-1" />
                                Labs
                            </Badge>
                            <Badge 
                                variant={visibleEvents.appointments ? "default" : "outline"}
                                className={`cursor-pointer ${visibleEvents.appointments ? 'bg-indigo-600 hover:bg-indigo-700' : 'border-indigo-600 text-indigo-500'}`}
                                onClick={() => toggleEventType('appointments')}
                                data-testid="filter-appointments"
                            >
                                <CalendarClock className="w-3 h-3 mr-1" />
                                Appointments
                            </Badge>
                        </div>
                    </div>
                </CardContent>
            </Card>
            
            {/* Treatment Events List - Color-coded by category */}
            <Card className="bg-slate-900 border-slate-700">
                <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                        <Pill className="w-5 h-5 text-green-500" />
                        Treatment Events Timeline
                    </CardTitle>
                    <CardDescription className="text-slate-400">
                        All treatments and health events with their dates
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {allEvents.length === 0 ? (
                        <div className="text-center py-8 text-slate-500">
                            <p>No treatment events logged in this time period.</p>
                            <p className="text-sm mt-1">Log supplements, medications, vaccines, or appointments to see them here.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {/* Supplements Column */}
                            {visibleEvents.supplements && groupedEvents.supplement.length > 0 && (
                                <div className="space-y-2" data-testid="supplements-list">
                                    <h4 className="font-semibold text-green-500 flex items-center gap-2 mb-3">
                                        <span className="w-2 h-2 rounded-full bg-green-500"></span>
                                        Supplements ({groupedEvents.supplement.length})
                                    </h4>
                                    <div className="space-y-1.5 max-h-48 overflow-y-auto">
                                        {groupedEvents.supplement.map((event, idx) => renderEventItem(event, idx))}
                                    </div>
                                </div>
                            )}
                            
                            {/* Medications Column */}
                            {visibleEvents.medications && groupedEvents.medication.length > 0 && (
                                <div className="space-y-2" data-testid="medications-list">
                                    <h4 className="font-semibold text-purple-500 flex items-center gap-2 mb-3">
                                        <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                                        Medications ({groupedEvents.medication.length})
                                    </h4>
                                    <div className="space-y-1.5 max-h-48 overflow-y-auto">
                                        {groupedEvents.medication.map((event, idx) => renderEventItem(event, idx))}
                                    </div>
                                </div>
                            )}
                            
                            {/* Flares Column */}
                            {visibleEvents.flares && groupedEvents.flare.length > 0 && (
                                <div className="space-y-2" data-testid="flares-list">
                                    <h4 className="font-semibold text-red-500 flex items-center gap-2 mb-3">
                                        <span className="w-2 h-2 rounded-full bg-red-500"></span>
                                        Flares ({groupedEvents.flare.length})
                                    </h4>
                                    <div className="space-y-1.5 max-h-48 overflow-y-auto">
                                        {groupedEvents.flare.map((event, idx) => renderEventItem(event, idx))}
                                    </div>
                                </div>
                            )}
                            
                            {/* Vaccines Column */}
                            {visibleEvents.vaccines && groupedEvents.vaccine.length > 0 && (
                                <div className="space-y-2" data-testid="vaccines-list">
                                    <h4 className="font-semibold text-teal-500 flex items-center gap-2 mb-3">
                                        <span className="w-2 h-2 rounded-full bg-teal-500"></span>
                                        Vaccines ({groupedEvents.vaccine.length})
                                    </h4>
                                    <div className="space-y-1.5 max-h-48 overflow-y-auto">
                                        {groupedEvents.vaccine.map((event, idx) => renderEventItem(event, idx))}
                                    </div>
                                </div>
                            )}
                            
                            {/* Lab Results Column */}
                            {visibleEvents.labs && groupedEvents.lab.length > 0 && (
                                <div className="space-y-2" data-testid="labs-list">
                                    <h4 className="font-semibold text-blue-500 flex items-center gap-2 mb-3">
                                        <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                                        Lab Results ({groupedEvents.lab.length})
                                    </h4>
                                    <div className="space-y-1.5 max-h-48 overflow-y-auto">
                                        {groupedEvents.lab.map((event, idx) => renderEventItem(event, idx))}
                                    </div>
                                </div>
                            )}
                            
                            {/* Appointments Column */}
                            {visibleEvents.appointments && groupedEvents.appointment.length > 0 && (
                                <div className="space-y-2" data-testid="appointments-list">
                                    <h4 className="font-semibold text-indigo-500 flex items-center gap-2 mb-3">
                                        <span className="w-2 h-2 rounded-full bg-indigo-500"></span>
                                        Appointments ({groupedEvents.appointment.length})
                                    </h4>
                                    <div className="space-y-1.5 max-h-48 overflow-y-auto">
                                        {groupedEvents.appointment.map((event, idx) => renderEventItem(event, idx))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    
                    {/* Legend */}
                    <div className="mt-6 pt-4 border-t border-slate-700">
                        <p className="text-slate-500 text-xs">
                            <span className="text-green-500">▶</span> Supplements &nbsp;
                            <span className="text-purple-500">▶</span> Medications &nbsp;
                            <span className="text-red-500">●</span> Flares &nbsp;
                            <span className="text-teal-500">▶</span> Vaccines &nbsp;
                            <span className="text-blue-500">◆</span> Labs &nbsp;
                            <span className="text-indigo-500">■</span> Appointments
                        </p>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

// Metrics chart colors matching the user's example
const METRICS_COLORS = {
    symptomSeverity: '#f97316', // orange - Behavioral/Symptom Severity
    flareSeverity: '#ef4444',   // red - Flare Severity
    sleepQuality: '#22c55e',    // green (dashed) - Sleep Quality
    cycleDay: '#8b5cf6'         // purple - Cycle/OCD Anxiety equivalent
};

// Custom tooltip for Metrics chart
const MetricsTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-slate-900 border border-slate-700 rounded-lg p-3 shadow-xl">
                <p className="text-white font-medium mb-2">{label}</p>
                {payload.map((entry, index) => {
                    const labels = {
                        symptomSeverity: 'Symptom Severity',
                        flareSeverity: 'Flare Severity',
                        sleepQuality: 'Sleep Quality',
                        cycleDay: 'Cycle Day'
                    };
                    return entry.value !== null && entry.value !== undefined && (
                        <p key={index} style={{ color: entry.color }} className="text-sm">
                            {labels[entry.dataKey] || entry.dataKey}: {entry.value}
                        </p>
                    );
                })}
            </div>
        );
    }
    return null;
};

// Metrics View Component - Multi-line correlation chart
const MetricsView = ({ timeline, loading }) => {
    const [visibleMetrics, setVisibleMetrics] = useState({
        symptomSeverity: true,
        flareSeverity: true,
        sleepQuality: true,
        cycleDay: true
    });

    // Process timeline data for the metrics chart
    const { metricsData, periodDays, flareDays } = React.useMemo(() => {
        const dataByDate = {};
        const periodDaysList = [];
        const flareDaysList = [];
        
        // Process all timeline entries
        timeline.forEach(entry => {
            const date = entry.date;
            if (!dataByDate[date]) {
                dataByDate[date] = {
                    date,
                    dateFormatted: format(parseISO(date), 'MMM d'),
                    symptomSeverity: null,
                    flareSeverity: null,
                    sleepQuality: null,
                    cycleDay: null,
                    isPeriod: false,
                    isFlare: false
                };
            }
            
            if (entry.type === 'symptom') {
                dataByDate[date].symptomSeverity = entry.severity || entry.overall_severity;
            }
            
            if (entry.type === 'flare') {
                dataByDate[date].flareSeverity = entry.severity;
                dataByDate[date].isFlare = true;
                flareDaysList.push(date);
            }
            
            if (entry.type === 'sleep') {
                // Sleep quality can be in different locations depending on API response
                dataByDate[date].sleepQuality = entry.data?.sleep_quality || entry.quality || entry.severity;
            }
            
            if (entry.type === 'cycle') {
                // Check if it's a period day - check multiple possible fields
                const isPeriodDay = entry.data?.is_period_day || 
                                   (entry.data?.flow_intensity && entry.data.flow_intensity !== 'none') ||
                                   (entry.flow && entry.flow !== 'none') ||
                                   entry.title?.toLowerCase().includes('period');
                
                if (isPeriodDay) {
                    dataByDate[date].isPeriod = true;
                    periodDaysList.push(date);
                }
                // Use cycle day number if available
                if (entry.cycle_day || entry.data?.cycle_day) {
                    dataByDate[date].cycleDay = entry.cycle_day || entry.data?.cycle_day;
                }
            }
        });
        
        // Convert to array and sort by date
        const sortedData = Object.values(dataByDate)
            .sort((a, b) => new Date(a.date) - new Date(b.date));
        
        return { 
            metricsData: sortedData, 
            periodDays: periodDaysList,
            flareDays: flareDaysList
        };
    }, [timeline]);

    // Generate reference areas for period and flare days
    const referenceAreas = React.useMemo(() => {
        const areas = [];
        
        // Find consecutive period days to create shaded areas
        let periodStart = null;
        let periodEnd = null;
        
        metricsData.forEach((item, index) => {
            if (item.isPeriod) {
                if (periodStart === null) {
                    periodStart = item.dateFormatted;
                }
                periodEnd = item.dateFormatted;
            } else if (periodStart !== null) {
                areas.push({
                    x1: periodStart,
                    x2: periodEnd,
                    type: 'period'
                });
                periodStart = null;
                periodEnd = null;
            }
        });
        
        // Don't forget the last period if it extends to the end
        if (periodStart !== null) {
            areas.push({
                x1: periodStart,
                x2: periodEnd,
                type: 'period'
            });
        }
        
        // Find consecutive flare days
        let flareStart = null;
        let flareEnd = null;
        
        metricsData.forEach((item, index) => {
            if (item.isFlare) {
                if (flareStart === null) {
                    flareStart = item.dateFormatted;
                }
                flareEnd = item.dateFormatted;
            } else if (flareStart !== null) {
                areas.push({
                    x1: flareStart,
                    x2: flareEnd,
                    type: 'flare'
                });
                flareStart = null;
                flareEnd = null;
            }
        });
        
        if (flareStart !== null) {
            areas.push({
                x1: flareStart,
                x2: flareEnd,
                type: 'flare'
            });
        }
        
        return areas;
    }, [metricsData]);

    const toggleMetric = (metric) => {
        setVisibleMetrics(prev => ({ ...prev, [metric]: !prev[metric] }));
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
        );
    }

    if (metricsData.length === 0) {
        return (
            <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                    <BarChart3 className="w-12 h-12 text-muted-foreground/50 mb-4" />
                    <h3 className="font-medium text-lg mb-2">No metrics data</h3>
                    <p className="text-muted-foreground text-center max-w-md">
                        Start logging symptoms, sleep, cycles, and flares to see your health metrics visualized here.
                    </p>
                </CardContent>
            </Card>
        );
    }

    return (
        <div className="space-y-6">
            <Card className="bg-slate-900 border-slate-700">
                <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-indigo-500" />
                        Health Metrics Correlation
                    </CardTitle>
                    <CardDescription className="text-slate-400">
                        Compare symptoms, sleep quality, cycles, and flares over time. 
                        <span className="text-pink-400"> Pink areas</span> indicate period days, 
                        <span className="text-red-400"> red areas</span> indicate flare days.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="h-[450px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart
                                data={metricsData}
                                margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                                <XAxis 
                                    dataKey="dateFormatted" 
                                    stroke="#9ca3af"
                                    tick={{ fill: '#9ca3af', fontSize: 12 }}
                                />
                                <YAxis 
                                    domain={[0, 10]} 
                                    stroke="#9ca3af"
                                    tick={{ fill: '#9ca3af', fontSize: 12 }}
                                    label={{ value: 'Severity / Quality', angle: -90, position: 'insideLeft', fill: '#9ca3af' }}
                                />
                                <Tooltip content={<MetricsTooltip />} />
                                
                                {/* Reference areas for period days (pink) */}
                                {referenceAreas.filter(a => a.type === 'period').map((area, index) => (
                                    <ReferenceArea
                                        key={`period-${index}`}
                                        x1={area.x1}
                                        x2={area.x2}
                                        fill="#ec4899"
                                        fillOpacity={0.15}
                                        stroke="#ec4899"
                                        strokeOpacity={0.3}
                                    />
                                ))}
                                
                                {/* Reference areas for flare days (red) */}
                                {referenceAreas.filter(a => a.type === 'flare').map((area, index) => (
                                    <ReferenceArea
                                        key={`flare-${index}`}
                                        x1={area.x1}
                                        x2={area.x2}
                                        fill="#ef4444"
                                        fillOpacity={0.15}
                                        stroke="#ef4444"
                                        strokeOpacity={0.3}
                                    />
                                ))}
                                
                                {/* Symptom Severity Line (Orange - solid) */}
                                {visibleMetrics.symptomSeverity && (
                                    <Line
                                        type="monotone"
                                        dataKey="symptomSeverity"
                                        stroke={METRICS_COLORS.symptomSeverity}
                                        strokeWidth={2}
                                        dot={{ fill: METRICS_COLORS.symptomSeverity, r: 4 }}
                                        connectNulls
                                        name="Symptom Severity"
                                    />
                                )}
                                
                                {/* Flare Severity Line (Red - solid) */}
                                {visibleMetrics.flareSeverity && (
                                    <Line
                                        type="monotone"
                                        dataKey="flareSeverity"
                                        stroke={METRICS_COLORS.flareSeverity}
                                        strokeWidth={2}
                                        dot={{ fill: METRICS_COLORS.flareSeverity, r: 5 }}
                                        connectNulls
                                        name="Flare Severity"
                                    />
                                )}
                                
                                {/* Sleep Quality Line (Green - dashed) */}
                                {visibleMetrics.sleepQuality && (
                                    <Line
                                        type="monotone"
                                        dataKey="sleepQuality"
                                        stroke={METRICS_COLORS.sleepQuality}
                                        strokeWidth={2}
                                        strokeDasharray="5 5"
                                        dot={{ fill: METRICS_COLORS.sleepQuality, r: 4, strokeWidth: 0 }}
                                        connectNulls
                                        name="Sleep Quality"
                                    />
                                )}
                                
                                {/* Cycle Day Line (Purple - solid) */}
                                {visibleMetrics.cycleDay && (
                                    <Line
                                        type="monotone"
                                        dataKey="cycleDay"
                                        stroke={METRICS_COLORS.cycleDay}
                                        strokeWidth={2}
                                        dot={{ fill: METRICS_COLORS.cycleDay, r: 4 }}
                                        connectNulls
                                        name="Cycle Day"
                                    />
                                )}
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>
                    
                    {/* Toggle Legend */}
                    <div className="mt-6 pt-4 border-t border-slate-700">
                        <p className="text-slate-400 text-sm mb-3">Toggle Metrics</p>
                        <div className="flex flex-wrap gap-2">
                            <Badge 
                                variant={visibleMetrics.symptomSeverity ? "default" : "outline"}
                                className={`cursor-pointer ${visibleMetrics.symptomSeverity ? 'bg-orange-600 hover:bg-orange-700' : 'border-orange-600 text-orange-500'}`}
                                onClick={() => toggleMetric('symptomSeverity')}
                                data-testid="toggle-symptom-severity"
                            >
                                <Activity className="w-3 h-3 mr-1" />
                                Symptom Severity
                            </Badge>
                            <Badge 
                                variant={visibleMetrics.flareSeverity ? "default" : "outline"}
                                className={`cursor-pointer ${visibleMetrics.flareSeverity ? 'bg-red-600 hover:bg-red-700' : 'border-red-600 text-red-500'}`}
                                onClick={() => toggleMetric('flareSeverity')}
                                data-testid="toggle-flare-severity"
                            >
                                <FlameKindling className="w-3 h-3 mr-1" />
                                Flare Severity
                            </Badge>
                            <Badge 
                                variant={visibleMetrics.sleepQuality ? "default" : "outline"}
                                className={`cursor-pointer ${visibleMetrics.sleepQuality ? 'bg-green-600 hover:bg-green-700' : 'border-green-600 text-green-500'}`}
                                onClick={() => toggleMetric('sleepQuality')}
                                data-testid="toggle-sleep-quality"
                            >
                                <BedDouble className="w-3 h-3 mr-1" />
                                Sleep Quality
                            </Badge>
                            <Badge 
                                variant={visibleMetrics.cycleDay ? "default" : "outline"}
                                className={`cursor-pointer ${visibleMetrics.cycleDay ? 'bg-purple-600 hover:bg-purple-700' : 'border-purple-600 text-purple-500'}`}
                                onClick={() => toggleMetric('cycleDay')}
                                data-testid="toggle-cycle-day"
                            >
                                <Heart className="w-3 h-3 mr-1" />
                                Cycle Day
                            </Badge>
                        </div>
                    </div>
                    
                    {/* Legend explanation */}
                    <div className="mt-4 p-3 rounded-lg bg-slate-800/50">
                        <p className="text-slate-400 text-xs">
                            <span className="inline-block w-4 h-1 bg-orange-500 mr-1"></span> Symptom Severity (solid) &nbsp;
                            <span className="inline-block w-4 h-1 bg-red-500 mr-1"></span> Flare Severity (solid) &nbsp;
                            <span className="inline-block w-4 border-t-2 border-dashed border-green-500 mr-1"></span> Sleep Quality (dashed) &nbsp;
                            <span className="inline-block w-4 h-1 bg-purple-500 mr-1"></span> Cycle Day (solid)
                        </p>
                        <p className="text-slate-500 text-xs mt-2">
                            <span className="inline-block w-3 h-3 bg-pink-500/30 border border-pink-500/50 mr-1"></span> Period days &nbsp;
                            <span className="inline-block w-3 h-3 bg-red-500/30 border border-red-500/50 mr-1"></span> Flare days
                        </p>
                    </div>
                </CardContent>
            </Card>
            
            {/* Quick Stats Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-orange-950/30 border-orange-800">
                    <CardContent className="p-4 text-center">
                        <Activity className="w-6 h-6 text-orange-500 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-orange-400">
                            {metricsData.filter(d => d.symptomSeverity !== null).length > 0 
                                ? (metricsData.filter(d => d.symptomSeverity !== null).reduce((sum, d) => sum + d.symptomSeverity, 0) / metricsData.filter(d => d.symptomSeverity !== null).length).toFixed(1)
                                : '-'
                            }
                        </p>
                        <p className="text-xs text-slate-400">Avg Symptom Severity</p>
                    </CardContent>
                </Card>
                <Card className="bg-red-950/30 border-red-800">
                    <CardContent className="p-4 text-center">
                        <FlameKindling className="w-6 h-6 text-red-500 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-red-400">
                            {metricsData.filter(d => d.isFlare).length}
                        </p>
                        <p className="text-xs text-slate-400">Flare Days</p>
                    </CardContent>
                </Card>
                <Card className="bg-green-950/30 border-green-800">
                    <CardContent className="p-4 text-center">
                        <BedDouble className="w-6 h-6 text-green-500 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-green-400">
                            {metricsData.filter(d => d.sleepQuality !== null).length > 0 
                                ? (metricsData.filter(d => d.sleepQuality !== null).reduce((sum, d) => sum + d.sleepQuality, 0) / metricsData.filter(d => d.sleepQuality !== null).length).toFixed(1)
                                : '-'
                            }
                        </p>
                        <p className="text-xs text-slate-400">Avg Sleep Quality</p>
                    </CardContent>
                </Card>
                <Card className="bg-pink-950/30 border-pink-800">
                    <CardContent className="p-4 text-center">
                        <Heart className="w-6 h-6 text-pink-500 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-pink-400">
                            {metricsData.filter(d => d.isPeriod).length}
                        </p>
                        <p className="text-xs text-slate-400">Period Days</p>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default function MedicalTimeline() {
    const { token } = useAuth();
    const [timeline, setTimeline] = useState([]);
    const [patterns, setPatterns] = useState(null);
    const [correlations, setCorrelations] = useState(null);
    const [loading, setLoading] = useState(true);
    const [correlationsLoading, setCorrelationsLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('timeline');
    const [filterType, setFilterType] = useState('all');
    const [daysRange, setDaysRange] = useState('30');
    const [expandedItems, setExpandedItems] = useState(new Set());

    const fetchTimeline = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/timeline?days=${daysRange}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (res.ok) {
                const data = await res.json();
                setTimeline(data || []);
            }
        } catch (error) {
            console.error('Failed to fetch timeline:', error);
            toast.error('Failed to load timeline');
        } finally {
            setLoading(false);
        }
    }, [token, daysRange]);

    const fetchPatterns = useCallback(async () => {
        try {
            const res = await fetch(`${API_URL}/api/timeline/patterns?days=${daysRange}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (res.ok) {
                const data = await res.json();
                setPatterns(data);
            }
        } catch (error) {
            console.error('Failed to fetch patterns:', error);
        }
    }, [token, daysRange]);

    const fetchCorrelations = useCallback(async () => {
        setCorrelationsLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/timeline/correlations?days=${daysRange}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (res.ok) {
                const data = await res.json();
                setCorrelations(data);
            }
        } catch (error) {
            console.error('Failed to fetch correlations:', error);
        } finally {
            setCorrelationsLoading(false);
        }
    }, [token, daysRange]);

    useEffect(() => {
        fetchTimeline();
        fetchPatterns();
        fetchCorrelations();
    }, [fetchTimeline, fetchPatterns, fetchCorrelations]);

    const toggleExpand = (id) => {
        setExpandedItems(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    // Filter timeline by type
    const filteredTimeline = filterType === 'all' 
        ? timeline 
        : timeline.filter(item => item.type === filterType);

    // Group timeline by date
    const groupedTimeline = filteredTimeline.reduce((groups, item) => {
        const date = item.date;
        if (!groups[date]) {
            groups[date] = [];
        }
        groups[date].push(item);
        return groups;
    }, {});

    const renderTimelineItem = (item) => {
        const config = TYPE_CONFIG[item.type] || TYPE_CONFIG.symptom;
        const Icon = config.icon;
        const isExpanded = expandedItems.has(item.id);

        return (
            <div 
                key={item.id} 
                className={`p-4 rounded-xl border ${config.bg} ${config.border} transition-all duration-200`}
                data-testid={`timeline-item-${item.id}`}
            >
                <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg bg-white dark:bg-gray-900 shadow-sm`}>
                        <Icon className={`w-4 h-4 ${config.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium">{item.title}</span>
                                <Badge variant="outline" className="text-xs">
                                    {config.label}
                                </Badge>
                                {item.severity && (
                                    <Badge 
                                        className={
                                            item.severity >= 7 ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                                            item.severity >= 4 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                                            'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                        }
                                    >
                                        Severity: {item.severity}/10
                                    </Badge>
                                )}
                            </div>
                            {item.details && (
                                <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="h-6 w-6 p-0"
                                    onClick={() => toggleExpand(item.id)}
                                >
                                    {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                </Button>
                            )}
                        </div>
                        {item.summary && (
                            <p className="text-sm text-muted-foreground mt-1">{item.summary}</p>
                        )}
                        {isExpanded && item.details && (
                            <div className="mt-3 pt-3 border-t border-current/10 text-sm space-y-1">
                                {typeof item.details === 'string' ? (
                                    <p className="text-muted-foreground">{item.details}</p>
                                ) : Array.isArray(item.details) ? (
                                    <ul className="list-disc list-inside text-muted-foreground space-y-1">
                                        {item.details.map((detail, idx) => (
                                            <li key={idx}>{typeof detail === 'object' ? detail.name || JSON.stringify(detail) : detail}</li>
                                        ))}
                                    </ul>
                                ) : (
                                    <pre className="text-xs text-muted-foreground overflow-x-auto">
                                        {JSON.stringify(item.details, null, 2)}
                                    </pre>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const renderPatternCard = (title, items, icon) => {
        const IconComponent = icon;
        if (!items || items.length === 0) return null;

        return (
            <Card>
                <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                        <IconComponent className="w-4 h-4" />
                        {title}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="space-y-2">
                        {items.map((item, idx) => (
                            <div key={idx} className="flex items-center justify-between">
                                <span className="text-sm">{item.name}</span>
                                <Badge variant="outline">{item.count}x</Badge>
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>
        );
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="font-heading text-3xl font-bold">Medical Timeline</h1>
                    <p className="text-muted-foreground mt-1">View all your health data in one unified timeline</p>
                </div>
                <div className="flex items-center gap-3">
                    <Select value={daysRange} onValueChange={setDaysRange}>
                        <SelectTrigger className="w-32" data-testid="days-filter">
                            <Clock className="w-4 h-4 mr-2" />
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="7">7 days</SelectItem>
                            <SelectItem value="14">14 days</SelectItem>
                            <SelectItem value="30">30 days</SelectItem>
                            <SelectItem value="60">60 days</SelectItem>
                            <SelectItem value="90">90 days</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Stats Summary */}
            {!loading && timeline.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                    {Object.entries(TYPE_CONFIG).map(([type, config]) => {
                        const count = timeline.filter(t => t.type === type).length;
                        const Icon = config.icon;
                        return (
                            <Card 
                                key={type} 
                                className={`cursor-pointer transition-all ${filterType === type ? 'ring-2 ring-primary' : ''}`}
                                onClick={() => setFilterType(filterType === type ? 'all' : type)}
                            >
                                <CardContent className="p-3">
                                    <div className="flex items-center gap-2">
                                        <Icon className={`w-4 h-4 ${config.color}`} />
                                        <span className="text-sm font-medium">{count}</span>
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-1">{config.label}</p>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            )}

            {/* Main Content */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="flex-wrap">
                    <TabsTrigger value="timeline">
                        <Clock className="w-4 h-4 mr-2" />
                        Timeline
                    </TabsTrigger>
                    <TabsTrigger value="graph" data-testid="graph-tab">
                        <LineChart className="w-4 h-4 mr-2" />
                        Graph
                    </TabsTrigger>
                    <TabsTrigger value="metrics" data-testid="metrics-tab">
                        <BarChart3 className="w-4 h-4 mr-2" />
                        Metrics
                    </TabsTrigger>
                    <TabsTrigger value="correlations" data-testid="correlations-tab">
                        <Zap className="w-4 h-4 mr-2" />
                        Smart Insights
                    </TabsTrigger>
                    <TabsTrigger value="patterns">
                        <Sparkles className="w-4 h-4 mr-2" />
                        Patterns
                    </TabsTrigger>
                </TabsList>

                {/* Graph Tab Content */}
                <TabsContent value="graph" className="mt-6">
                    <GraphView 
                        timeline={timeline}
                        supplements={correlations?.supplement_impact || []}
                        flares={patterns?.flare_data || []}
                        loading={loading}
                        dateRange={daysRange}
                        token={token}
                        onRefresh={fetchTimeline}
                    />
                </TabsContent>

                {/* Metrics Tab Content */}
                <TabsContent value="metrics" className="mt-6" data-testid="metrics-content">
                    <MetricsView 
                        timeline={timeline}
                        loading={loading}
                    />
                </TabsContent>

                <TabsContent value="timeline" className="mt-6">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                        </div>
                    ) : filteredTimeline.length === 0 ? (
                        <Card>
                            <CardContent className="flex flex-col items-center justify-center py-12">
                                <Clock className="w-12 h-12 text-muted-foreground/50 mb-4" />
                                <h3 className="font-medium text-lg mb-2">No entries yet</h3>
                                <p className="text-muted-foreground text-center max-w-md">
                                    Start tracking your symptoms, sleep, food, and more to see your unified health timeline here.
                                </p>
                            </CardContent>
                        </Card>
                    ) : (
                        <div className="space-y-6">
                            {Object.entries(groupedTimeline)
                                .sort(([a], [b]) => b.localeCompare(a))
                                .map(([date, items]) => (
                                    <div key={date} data-testid={`timeline-date-${date}`}>
                                        <div className="flex items-center gap-3 mb-4">
                                            <div className="h-px flex-1 bg-border" />
                                            <span className="text-sm font-medium text-muted-foreground">
                                                {format(parseISO(date), 'EEEE, MMMM d, yyyy')}
                                            </span>
                                            <div className="h-px flex-1 bg-border" />
                                        </div>
                                        <div className="space-y-3 pl-4 border-l-2 border-muted">
                                            {items.map(renderTimelineItem)}
                                        </div>
                                    </div>
                                ))}
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="correlations" className="mt-6" data-testid="correlations-content">
                    {correlationsLoading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                        </div>
                    ) : !correlations ? (
                        <Card>
                            <CardContent className="flex flex-col items-center justify-center py-12">
                                <Search className="w-12 h-12 text-muted-foreground/50 mb-4" />
                                <h3 className="font-medium text-lg mb-2">Analyzing your data...</h3>
                                <p className="text-muted-foreground text-center max-w-md">
                                    We're looking for patterns in your health data.
                                </p>
                            </CardContent>
                        </Card>
                    ) : (
                        <div className="space-y-6">
                            {/* Data Quality Banner */}
                            {correlations.data_quality && !correlations.data_quality.sufficient_data && (
                                <Card className="border-amber-200 bg-amber-50/50 dark:bg-amber-950/20 dark:border-amber-800">
                                    <CardContent className="p-4">
                                        <div className="flex items-start gap-3">
                                            <Info className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                                            <div>
                                                <p className="font-medium text-amber-800 dark:text-amber-200">Keep Tracking!</p>
                                                <p className="text-sm text-amber-700 dark:text-amber-300">
                                                    You have {correlations.data_quality.symptom_entries} symptom entries, {correlations.data_quality.sleep_entries} sleep entries, and {correlations.data_quality.food_entries} food entries. 
                                                    We need at least 7 days of symptom tracking plus sleep or food data to find meaningful patterns.
                                                </p>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            )}

                            {/* Key Insights */}
                            {correlations.insights?.length > 0 && (
                                <div className="space-y-4">
                                    <h3 className="font-heading font-semibold text-lg flex items-center gap-2">
                                        <Zap className="w-5 h-5 text-amber-500" />
                                        Key Insights
                                    </h3>
                                    <div className="grid gap-4">
                                        {correlations.insights.map((insight, idx) => {
                                            const iconMap = {
                                                'alert-triangle': AlertTriangle,
                                                'moon': Moon,
                                                'pill': Pill,
                                                'flame': Flame,
                                                'trending-down': TrendingDown,
                                                'info': Info,
                                                'search': Search
                                            };
                                            const InsightIcon = iconMap[insight.icon] || Info;
                                            
                                            return (
                                                <Card 
                                                    key={idx} 
                                                    className={`
                                                        ${insight.type === 'warning' ? 'border-amber-200 bg-amber-50/50 dark:bg-amber-950/20 dark:border-amber-800' : ''}
                                                        ${insight.type === 'positive' ? 'border-emerald-200 bg-emerald-50/50 dark:bg-emerald-950/20 dark:border-emerald-800' : ''}
                                                        ${insight.type === 'info' ? 'border-blue-200 bg-blue-50/50 dark:bg-blue-950/20 dark:border-blue-800' : ''}
                                                    `}
                                                    data-testid={`insight-${idx}`}
                                                >
                                                    <CardContent className="p-4">
                                                        <div className="flex items-start gap-4">
                                                            <div className={`p-2 rounded-lg ${
                                                                insight.type === 'warning' ? 'bg-amber-100 dark:bg-amber-900/50' :
                                                                insight.type === 'positive' ? 'bg-emerald-100 dark:bg-emerald-900/50' :
                                                                'bg-blue-100 dark:bg-blue-900/50'
                                                            }`}>
                                                                <InsightIcon className={`w-5 h-5 ${
                                                                    insight.type === 'warning' ? 'text-amber-600 dark:text-amber-400' :
                                                                    insight.type === 'positive' ? 'text-emerald-600 dark:text-emerald-400' :
                                                                    'text-blue-600 dark:text-blue-400'
                                                                }`} />
                                                            </div>
                                                            <div className="flex-1">
                                                                <div className="flex items-center justify-between gap-2 mb-1">
                                                                    <h4 className="font-semibold">{insight.title}</h4>
                                                                    {insight.confidence > 0 && (
                                                                        <Badge variant="outline" className="text-xs">
                                                                            {insight.confidence}% confidence
                                                                        </Badge>
                                                                    )}
                                                                </div>
                                                                <p className="text-sm text-muted-foreground">{insight.description}</p>
                                                                {insight.action && (
                                                                    <p className="text-sm font-medium mt-2 text-primary">
                                                                        💡 {insight.action}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </CardContent>
                                                </Card>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Food-Symptom Correlations */}
                            {correlations.food_symptom_correlations?.length > 0 && (
                                <Card>
                                    <CardHeader>
                                        <CardTitle className="text-base flex items-center gap-2">
                                            <Utensils className="w-5 h-5 text-emerald-500" />
                                            Food Impact Analysis
                                        </CardTitle>
                                        <CardDescription>
                                            How different foods affect your symptoms 1-2 days later
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="space-y-3">
                                            {correlations.food_symptom_correlations.slice(0, 8).map((food, idx) => (
                                                <div key={idx} className="flex items-center gap-3">
                                                    <div className={`p-1.5 rounded ${
                                                        food.direction === 'worse' ? 'bg-red-100 dark:bg-red-900/30' : 'bg-emerald-100 dark:bg-emerald-900/30'
                                                    }`}>
                                                        {food.direction === 'worse' ? (
                                                            <TrendingUp className="w-4 h-4 text-red-500" />
                                                        ) : (
                                                            <TrendingDown className="w-4 h-4 text-emerald-500" />
                                                        )}
                                                    </div>
                                                    <div className="flex-1">
                                                        <div className="flex items-center justify-between">
                                                            <span className="font-medium">{food.food}</span>
                                                            <span className={`text-sm font-semibold ${
                                                                food.direction === 'worse' ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'
                                                            }`}>
                                                                {food.direction === 'worse' ? '+' : ''}{food.impact.toFixed(1)} severity
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center gap-2 mt-1">
                                                            <Progress 
                                                                value={Math.min(100, Math.abs(food.impact) * 20)} 
                                                                className={`h-1.5 flex-1 ${food.direction === 'worse' ? '[&>div]:bg-red-500' : '[&>div]:bg-emerald-500'}`}
                                                            />
                                                            <span className="text-xs text-muted-foreground">{food.occurrences}x</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </CardContent>
                                </Card>
                            )}

                            {/* Sleep Impact */}
                            {correlations.sleep_impact && (
                                <Card>
                                    <CardHeader>
                                        <CardTitle className="text-base flex items-center gap-2">
                                            <Moon className="w-5 h-5 text-indigo-500" />
                                            Sleep Impact on Symptoms
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="p-4 rounded-xl bg-red-50 dark:bg-red-950/30 text-center">
                                                <p className="text-3xl font-bold text-red-600 dark:text-red-400">
                                                    {correlations.sleep_impact.poor_sleep_avg_severity}
                                                </p>
                                                <p className="text-sm text-muted-foreground mt-1">Avg severity after poor sleep</p>
                                                <p className="text-xs text-muted-foreground">({correlations.sleep_impact.poor_sleep_nights} nights)</p>
                                            </div>
                                            <div className="p-4 rounded-xl bg-emerald-50 dark:bg-emerald-950/30 text-center">
                                                <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">
                                                    {correlations.sleep_impact.good_sleep_avg_severity}
                                                </p>
                                                <p className="text-sm text-muted-foreground mt-1">Avg severity after good sleep</p>
                                                <p className="text-xs text-muted-foreground">({correlations.sleep_impact.good_sleep_nights} nights)</p>
                                            </div>
                                        </div>
                                        {correlations.sleep_impact.impact > 0 && (
                                            <p className="text-sm text-center mt-4 text-muted-foreground">
                                                Poor sleep is associated with <strong className="text-red-600 dark:text-red-400">+{correlations.sleep_impact.impact}</strong> symptom severity
                                            </p>
                                        )}
                                    </CardContent>
                                </Card>
                            )}

                            {/* Supplement Adherence Impact */}
                            {correlations.supplement_adherence_impact && (
                                <Card>
                                    <CardHeader>
                                        <CardTitle className="text-base flex items-center gap-2">
                                            <Pill className="w-5 h-5 text-purple-500" />
                                            Supplement Adherence Impact
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="p-4 rounded-xl bg-emerald-50 dark:bg-emerald-950/30 text-center">
                                                <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">
                                                    {correlations.supplement_adherence_impact.high_adherence_avg_severity}
                                                </p>
                                                <p className="text-sm text-muted-foreground mt-1">Avg severity (80%+ adherence)</p>
                                                <p className="text-xs text-muted-foreground">({correlations.supplement_adherence_impact.high_adherence_days} days)</p>
                                            </div>
                                            <div className="p-4 rounded-xl bg-amber-50 dark:bg-amber-950/30 text-center">
                                                <p className="text-3xl font-bold text-amber-600 dark:text-amber-400">
                                                    {correlations.supplement_adherence_impact.low_adherence_avg_severity}
                                                </p>
                                                <p className="text-sm text-muted-foreground mt-1">Avg severity (&lt;50% adherence)</p>
                                                <p className="text-xs text-muted-foreground">({correlations.supplement_adherence_impact.low_adherence_days} days)</p>
                                            </div>
                                        </div>
                                        {correlations.supplement_adherence_impact.impact > 0 && (
                                            <p className="text-sm text-center mt-4 text-muted-foreground">
                                                Consistent supplement use is associated with <strong className="text-emerald-600 dark:text-emerald-400">-{correlations.supplement_adherence_impact.impact}</strong> symptom severity
                                            </p>
                                        )}
                                    </CardContent>
                                </Card>
                            )}

                            {/* Cycle Impact */}
                            {correlations.cycle_impact && (
                                <Card>
                                    <CardHeader>
                                        <CardTitle className="text-base flex items-center gap-2">
                                            <Heart className="w-5 h-5 text-pink-500" />
                                            Menstrual Cycle Impact
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="p-4 rounded-xl bg-pink-50 dark:bg-pink-950/30 text-center">
                                                <p className="text-3xl font-bold text-pink-600 dark:text-pink-400">
                                                    {correlations.cycle_impact.period_avg_severity}
                                                </p>
                                                <p className="text-sm text-muted-foreground mt-1">Avg severity during period</p>
                                                <p className="text-xs text-muted-foreground">({correlations.cycle_impact.period_days_analyzed} days)</p>
                                            </div>
                                            <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-950/30 text-center">
                                                <p className="text-3xl font-bold text-gray-600 dark:text-gray-400">
                                                    {correlations.cycle_impact.non_period_avg_severity}
                                                </p>
                                                <p className="text-sm text-muted-foreground mt-1">Avg severity other days</p>
                                                <p className="text-xs text-muted-foreground">({correlations.cycle_impact.non_period_days_analyzed} days)</p>
                                            </div>
                                        </div>
                                        {correlations.cycle_impact.impact > 0 && (
                                            <p className="text-sm text-center mt-4 text-muted-foreground">
                                                Your period is associated with <strong className="text-pink-600 dark:text-pink-400">+{correlations.cycle_impact.impact}</strong> symptom severity
                                            </p>
                                        )}
                                    </CardContent>
                                </Card>
                            )}

                            {/* Trigger & Positive Patterns Summary */}
                            <div className="grid md:grid-cols-2 gap-4">
                                {correlations.trigger_patterns?.length > 0 && (
                                    <Card className="border-red-200 dark:border-red-800">
                                        <CardHeader className="pb-2">
                                            <CardTitle className="text-base flex items-center gap-2 text-red-600 dark:text-red-400">
                                                <XCircle className="w-4 h-4" />
                                                Potential Triggers
                                            </CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <div className="space-y-2">
                                                {correlations.trigger_patterns.map((trigger, idx) => (
                                                    <div key={idx} className="flex items-center justify-between p-2 rounded bg-red-50 dark:bg-red-950/30">
                                                        <div className="flex items-center gap-2">
                                                            <Badge variant="outline" className="text-xs capitalize">{trigger.type}</Badge>
                                                            <span className="text-sm font-medium">{trigger.trigger}</span>
                                                        </div>
                                                        <span className="text-sm text-red-600 dark:text-red-400">+{trigger.impact.toFixed(1)}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </CardContent>
                                    </Card>
                                )}

                                {correlations.positive_patterns?.length > 0 && (
                                    <Card className="border-emerald-200 dark:border-emerald-800">
                                        <CardHeader className="pb-2">
                                            <CardTitle className="text-base flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
                                                <CheckCircle2 className="w-4 h-4" />
                                                What's Helping
                                            </CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <div className="space-y-2">
                                                {correlations.positive_patterns.map((pattern, idx) => (
                                                    <div key={idx} className="flex items-center justify-between p-2 rounded bg-emerald-50 dark:bg-emerald-950/30">
                                                        <div className="flex items-center gap-2">
                                                            <Badge variant="outline" className="text-xs capitalize">{pattern.type}</Badge>
                                                            <span className="text-sm font-medium">{pattern.factor}</span>
                                                        </div>
                                                        <span className="text-sm text-emerald-600 dark:text-emerald-400">-{pattern.benefit.toFixed(1)}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </CardContent>
                                    </Card>
                                )}
                            </div>
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="patterns" className="mt-6">
                    {!patterns ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                        </div>
                    ) : (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {renderPatternCard("Common Symptoms", patterns.common_symptoms, Activity)}
                            {renderPatternCard("Food Reactions", patterns.food_reaction_triggers, Utensils)}
                            {renderPatternCard("Flare Triggers", patterns.flare_triggers, FlameKindling)}

                            {/* Severity by Day of Week */}
                            {patterns.severity_by_day_of_week && Object.keys(patterns.severity_by_day_of_week).length > 0 && (
                                <Card className="md:col-span-2 lg:col-span-3">
                                    <CardHeader className="pb-3">
                                        <CardTitle className="text-base flex items-center gap-2">
                                            <TrendingUp className="w-4 h-4" />
                                            Severity by Day of Week
                                        </CardTitle>
                                        <CardDescription>
                                            See which days tend to have higher symptom severity
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="grid grid-cols-7 gap-2">
                                            {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => {
                                                const data = patterns.severity_by_day_of_week[day];
                                                const avg = data?.average || 0;
                                                const height = avg ? Math.max(20, (avg / 10) * 100) : 20;
                                                
                                                return (
                                                    <div key={day} className="text-center">
                                                        <div className="h-24 flex items-end justify-center mb-2">
                                                            <div 
                                                                className={`w-full max-w-8 rounded-t transition-all ${
                                                                    avg >= 7 ? 'bg-red-400' :
                                                                    avg >= 4 ? 'bg-amber-400' :
                                                                    avg > 0 ? 'bg-emerald-400' : 'bg-gray-200 dark:bg-gray-700'
                                                                }`}
                                                                style={{ height: `${height}%` }}
                                                            />
                                                        </div>
                                                        <p className="text-xs font-medium">{day.slice(0, 3)}</p>
                                                        <p className="text-xs text-muted-foreground">
                                                            {avg ? avg.toFixed(1) : '-'}
                                                        </p>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </CardContent>
                                </Card>
                            )}

                            {/* Insights Card */}
                            <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-950/30 dark:to-purple-950/20">
                                <CardHeader>
                                    <CardTitle className="text-base flex items-center gap-2">
                                        <Sparkles className="w-4 h-4 text-indigo-500" />
                                        Insights
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="text-sm space-y-3">
                                    {patterns.common_symptoms?.length > 0 && (
                                        <p>
                                            Your most frequent symptom is <strong>{patterns.common_symptoms[0].name}</strong> 
                                            ({patterns.common_symptoms[0].count} occurrences).
                                        </p>
                                    )}
                                    {patterns.food_reaction_triggers?.length > 0 && (
                                        <p className="flex items-start gap-2">
                                            <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                                            <span>
                                                <strong>{patterns.food_reaction_triggers[0].name}</strong> has caused the most reactions. 
                                                Consider tracking this food more carefully.
                                            </span>
                                        </p>
                                    )}
                                    {(!patterns.common_symptoms?.length && !patterns.food_reaction_triggers?.length) && (
                                        <p className="text-muted-foreground">
                                            Keep tracking your health data to discover patterns and insights.
                                        </p>
                                    )}
                                </CardContent>
                            </Card>
                        </div>
                    )}
                </TabsContent>
            </Tabs>
        </div>
    );
}
